import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface SubtaskDialogProps {
  open: boolean;
  onClose: () => void;
  taskId: string;
  unaId: string | null;
  subtask?: any;
  onSave: () => void;
}

export function SubtaskDialog({ open, onClose, taskId, unaId, subtask, onSave }: SubtaskDialogProps) {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<'not_started' | 'in_progress' | 'completed' | 'blocked'>('not_started');
  const [assigneeId, setAssigneeId] = useState<string>("");
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [members, setMembers] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && unaId) {
      loadMembers();
    }
    if (subtask) {
      setTitle(subtask.title || "");
      setDescription(subtask.description || "");
      setStatus(subtask.status || 'not_started');
      setAssigneeId(subtask.assignee_id || "");
      setDueDate(subtask.due_date ? new Date(subtask.due_date) : undefined);
    } else {
      // Reset form
      setTitle("");
      setDescription("");
      setStatus('not_started');
      setAssigneeId("");
      setDueDate(undefined);
    }
  }, [open, subtask, unaId]);

  const loadMembers = async () => {
    if (!unaId) return;
    
    try {
      const { data, error } = await supabase
        .from('una_members')
        .select('id, full_name, member_role, user_id')
        .eq('una_id', unaId)
        .eq('status', 'active')
        .order('full_name');

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error('Error loading members:', error);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast({
        title: "Error",
        description: "Please enter a subtask title",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const normalizeUuid = (val?: string | null) => {
        const v = (val || '').trim();
        return /^[0-9a-fA-F-]{36}$/.test(v) ? v : null;
      };

      const subtaskData = {
        task_id: taskId,
        title: title.trim(),
        description: description.trim() || null,
        status,
        assignee_id: normalizeUuid(assigneeId),
        due_date: dueDate?.toISOString().split('T')[0] || null,
      } as const;

      console.log('Saving subtask with payload:', subtaskData);

      if (subtask) {
        // Update existing
        const { error } = await supabase
          .from('task_subtasks')
          .update(subtaskData)
          .eq('id', subtask.id);

        if (error) throw error;
      } else {
        // Create new - get max order
        const { data: maxOrder } = await supabase
          .from('task_subtasks')
          .select('order_number')
          .eq('task_id', taskId)
          .order('order_number', { ascending: false })
          .limit(1)
          .maybeSingle();

        const newOrderNumber = (maxOrder?.order_number || 0) + 1;
        
        const { error } = await supabase
          .from('task_subtasks')
          .insert([{
            ...subtaskData,
            order_number: newOrderNumber,
            created_by: (user?.id as string),
          }]);

        if (error) throw error;

        // Send notification if assignee is set (best-effort)
        if (assigneeId) {
          const member = members.find(m => m.id === assigneeId);
          if (member?.user_id) {
            const { error: notifError } = await supabase.from('member_notifications').insert({
              una_id: unaId!,
              user_id: member.user_id,
              type: 'subtask_assigned',
              title: 'New Subtask Assigned',
              message: `You've been assigned: ${title}`,
              action_required: true,
            });
            if (notifError) {
              console.warn('Skipping notification insert due to policy/error:', notifError);
            }
          }
        }
      }

      toast({
        title: "Success",
        description: subtask ? "Subtask updated" : "Subtask created",
      });

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving subtask:', error);
      toast({
        title: "Error",
        description: "Failed to save subtask",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{subtask ? 'Edit Subtask' : 'Add Subtask'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter subtask title"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details (optional)"
              rows={3}
            />
          </div>

          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={(val: any) => setStatus(val)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_started">Not Started</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {unaId && members.length > 0 && (
            <div>
              <Label>Assignee (Optional)</Label>
              <Select value={assigneeId || undefined} onValueChange={(val) => setAssigneeId(val)}>
                <SelectTrigger>
                  <SelectValue placeholder="No assignee" />
                </SelectTrigger>
                <SelectContent>
                  {members.filter(m => m.id && m.full_name).map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.full_name} ({member.member_role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Due Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !dueDate && "text-muted-foreground")}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, "PPP") : "Pick a date (optional)"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={dueDate} onSelect={setDueDate} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
